import { ListItemButton, Tooltip, Typography } from '@ethings/ui-toolkit';
import { styled } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useAuthenticated } from '../../auth';
import { isPathActive, useCustomRoutes, useSwitchAwareConfig } from '../../hooks';
import { getLocalStorageItem, hotjarEvent } from '../../util';
import { useBoolFlag } from '../flags';
import { Icon } from '../icons';
import { SideMenuDashboardMenuItemDefinition, SideMenuItemDefinition, SideMenuPortalVersionMenuItemDefinition } from './side-menu.types';
import { resolveSideMenuToDefinition, sideMenuTransitionStyles } from './side-menu.utils';

interface SideMenuItemProps {
  menuItem: SideMenuItemDefinition;
  isOpen: boolean;
}

export const SideMenuItem: FC<SideMenuItemProps> = ({ menuItem, isOpen }) => {
  const customRoutes = useCustomRoutes();
  const { t } = useTranslation();
  const config = useSwitchAwareConfig();

  const commonProps = {
    isOpen,
    testId: menuItem.testId,
    icon: menuItem.icon,
    openInNewTab: menuItem.openInNewTab,
  };

  switch (menuItem.type) {
    case 'general':
    case 'alerts': {
      const to = resolveSideMenuToDefinition(menuItem.to, customRoutes, config);
      const activePaths = menuItem.activePaths?.map((activePath) => resolveSideMenuToDefinition(activePath, customRoutes, config));
      const ignoredPaths = menuItem.ignoredPaths?.map((activePath) => resolveSideMenuToDefinition(activePath, customRoutes, config));

      return (
        <BaseMenuItem
          label={t(menuItem.label)}
          to={to}
          activePaths={activePaths ?? []}
          ignoredPaths={ignoredPaths ?? []}
          onClick={menuItem.type === 'alerts' ? () => {
            if (location.pathname === `/${customRoutes.alerts}`) return;
            localStorage.removeItem('alert-list');
            localStorage.removeItem('alert-list-range');
          } : undefined}
          {...commonProps}
        />
      );
    }

    case 'dashboard': {
      return (
        <DashboardMenuItem
          menuItem={menuItem}
          {...commonProps}
        />
      );
    }

    case 'help': {
      return (
        <BaseMenuItem
          label={t(menuItem.label)}
          onClick={() => {
            window.Intercom('show');
          }}
          {...commonProps}
        />
      );
    }

    case 'feedback': {
      return (
        <BaseMenuItem
          label={t(menuItem.label)}
          onClick={() => {
            if (menuItem.hotjarAction) {
              hotjarEvent(menuItem.hotjarAction);
            }
          }}
          {...commonProps}
        />
      );
    }

    case 'portal-version': {
      return (
        <PortalVersionMenuItem
          menuItem={menuItem}
          {...commonProps}
        />
      );
    }

    case 'templated-page': {
      return (
        <BaseMenuItem
          label={t(menuItem.label)}
          to={`/view/${menuItem.templateId}?title=${encodeURIComponent(t(menuItem.label))}`}
          activePaths={[`/view/${menuItem.templateId}`]}
          {...commonProps}
        />
      );
    }
  }
};

const PortalVersionMenuItem: FC<{ menuItem: SideMenuPortalVersionMenuItemDefinition; isOpen: boolean }> = ({ isOpen, menuItem }) => {
  const { t } = useTranslation(['common']);

  const build = import.meta.env.VITE_BUILD || import.meta.env.REACT_APP_BUILD;

  const getTooltip = (): string => {
    const buildTimestamp = import.meta.env.VITE_BUILD_DATE || import.meta.env.REACT_APP_BUILD_DATE;
    if (!buildTimestamp) return t('common:component.navigation.update.labels.up-to-date');

    const buildDate = DateTime.fromISO(buildTimestamp);
    if (!buildDate.isValid) return t('common:component.navigation.update.labels.up-to-date');
    return buildDate.toLocal().toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  };

  return (
    <Tooltip
      data-testid="portal-version-date"
      title={getTooltip()}
      placement="top"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10],
              },
            },
          ],
          ...({ 'data-testid': 'portal-version-date' }),
        },
      }}
    >
      <span>
        <BaseMenuItem
          label={t('common:common.labels.version', { build: build ?? t('common:component.navigation.update.labels.manualBuild') })}
          isOpen={isOpen}
          icon={menuItem.icon}
          testId={menuItem.testId}
        />
      </span>
    </Tooltip>
  );
};

interface DashboardMenuItemProps {
  menuItem: SideMenuDashboardMenuItemDefinition;
  isOpen: boolean;
}

const DashboardMenuItem: FC<DashboardMenuItemProps> = ({ menuItem, isOpen }) => {
  const { t } = useTranslation();
  const customRoutes = useCustomRoutes();
  const { restClient } = useAuthenticated();
  const v2DashSwitching = useBoolFlag('portals-dashboard-switching-enhancements-temporary-20240918');
  const navigate = useNavigate();

  const getActiveDashboardId = async (type: 'report' | 'dashboard' | 'analytic'): Promise<string> => {
    const currentDashboards: Record<string, string> = getLocalStorageItem('current-dashboards');
    const constructPath = (base: string, id: string | null): string => `/${base}/${id ?? ''}`;
    switch (type) {
      case 'report':
        if (!currentDashboards.reports) {
          const firstId = await getFirstDashboardId(type);
          return constructPath(customRoutes.reports, firstId);
        }
        return constructPath(customRoutes.reports, currentDashboards.reports);
      case 'dashboard':
        if (!currentDashboards.dashboards) {
          const firstId = await getFirstDashboardId(type);
          return constructPath(customRoutes.dashboards, firstId);
        }
        return constructPath(customRoutes.dashboards, currentDashboards.dashboards);
      case 'analytic':
        if (!currentDashboards.analytics) {
          const firstId = await getFirstDashboardId(type);
          return constructPath(customRoutes.analytics, firstId);
        }
        return constructPath(customRoutes.analytics, currentDashboards.analytics);
    }
  };

  const getFirstDashboardId = async (type: string): Promise<string | null> => {
    try {
      const response = await restClient.my.dashboards.getAllV2({
        sort: JSON.stringify({ display: 'asc' }),
        filter: { type },
        limit: 1,
      });
      return response.items?.[0]?._id || null;
    } catch (error) {
      console.error('[getFirstDashboardId] Failed to fetch dashboards:', error);
      return null;
    }
  };

  return (
    <BaseMenuItem
      icon={menuItem.icon}
      label={t(menuItem.label)}
      to={`/${customRoutes[menuItem.subtype]}`}
      testId={menuItem.testId}
      activePaths={[`/${customRoutes[menuItem.subtype]}/:id`]}
      isOpen={isOpen}
      onClick={
        v2DashSwitching ?
          async (e) => {
            e.preventDefault();
            const typeMap = {
              dashboards: 'dashboard',
              analytics: 'analytic',
              reports: 'report',
            } as const;
            const path = await getActiveDashboardId(typeMap[menuItem.subtype]);
            navigate(path);
          } : undefined
      }
    />
  );
};

interface BaseMenuItemProps {
  label: string;
  icon: string;
  isOpen: boolean;
  to?: string;
  activePaths?: string[];
  ignoredPaths?: string[];
  testId?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  openInNewTab?: boolean;
}

const BaseMenuItem: FC<BaseMenuItemProps> = ({ to, icon, label, activePaths, ignoredPaths, isOpen, testId, onClick, openInNewTab }) => {
  const location = useLocation();
  const isActive = !ignoredPaths?.includes(location.pathname) && isPathActive([...(to ? [to] : []), ...(activePaths ?? [])], location.pathname);
  const Component = to ? (openInNewTab ? 'a' : Link) : undefined;
  const hasAction = Boolean(to || onClick);

  return (
    <BaseMenuItemRoot
      onClick={onClick}
      isActive={isActive}
      isOpen={isOpen}
      component={Component}
      // @ts-expect-error component prop is not handled correctly
      to={Component === 'a' ? undefined : to}
      href={Component === 'a' ? to : undefined}
      hasAction={hasAction}
      data-testid={testId}
      target={openInNewTab ? '_blank' : undefined}
    >
      <Icon icon={icon} />
      <BaseMenuItemLabel style={{ opacity: isOpen ? 1 : 0 }} variant='body2' component="span">{label}</BaseMenuItemLabel>
    </BaseMenuItemRoot>
  );
};

const BaseMenuItemLabel = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  ...sideMenuTransitionStyles,
});

const BaseMenuItemRoot = styled(ListItemButton)<{ isActive?: boolean; isOpen: boolean; hasAction: boolean }>(({ isActive, isOpen, hasAction, theme }) => ({
  gap: theme.spacing(1),
  backgroundColor: isActive ? `${theme.sidebar.activeMenuItem.background} !important` : undefined,
  color: isActive ? `${theme.sidebar.activeMenuItem.text} !important` : undefined,
  borderRadius: `${typeof theme.shape.borderRadius === 'string' ? theme.shape.borderRadius : `${theme.shape.borderRadius}px`} !important`,
  paddingLeft: theme.spacing(isOpen ? 2 : 1),
  paddingRight: theme.spacing(isOpen ? 2 : 1),
  cursor: hasAction ? undefined : 'default !important',
  '&:hover, &:focus-visible': {
    backgroundColor: `${isActive ? theme.sidebar.activeMenuItem.background : theme.sidebar.secondaryBackground} !important`,
  },
  '& svg': {
    marginLeft: '1px',
    fontSize: '1.5em',
    flexShrink: 0,
    color: isActive ? theme.sidebar.activeMenuItem.text : theme.sidebar.icons.color,
  },
  ...theme.typography.caption,
  ...sideMenuTransitionStyles,
}));
